import { useMemo, type ReactElement } from 'react'
import { useRequiredParams } from '@util'
import { Logger, type QuizId } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { useQuiz, useQuizFeature } from '@front/model'
import {
  ErrorScreen,
  NestedNavigation,
  NotReady,
  Page,
  PermissionCheckWrapper,
  type PageRoute,
} from '@components'
import { QuizContext, type QuizContextType } from './context'

export type WithQuizId = {
  quizId: QuizId
}

const logger = new Logger('QuizPage')
// logger.level = 'verbose'

function QuizPage(): ReactElement {
  const { quizId } = useRequiredParams<WithQuizId>()
  const { data, isLoading, error, refetch, isFetching } = useQuiz(quizId)
  const { milestone, isLoading: isLoadingFeature, isEnabled } = useQuizFeature()

  const context = useMemo(() => {
    if (!data) return
    return { ...data, refetch, isFetching } satisfies QuizContextType
  }, [refetch, data, isFetching])

  if (isLoading || isLoadingFeature || error || !context) {
    return <NotReady data={data} error={error} id='Quiz' isLoading={isLoading || isLoadingFeature} />
  }
  if (!isEnabled) {
    return <ErrorScreen message='This feature is not available.' />
  }

  const { quiz } = context

  const routes: PageRoute[] = [
    {
      label: 'Settings',
      path: `/quiz/${quizId}/settings`,
      icon: 'settings',
    },
    {
      label: 'Questions',
      path: `/quiz/${quizId}/questions`,
      icon: 'question_mark',
    },
    {
      label: 'Assignees',
      path: `/quiz/${quizId}/assignees`,
      icon: 'assignment',
    },
    {
      label: 'Summary',
      path: `/quiz/${quizId}/summary`,
      icon: 'table',
    },
  ]

  logger.verbose('rendering quiz page', { data })

  return (
    <PermissionCheckWrapper permissionType='quiz:manage' targetId={[quiz.ownerId, ...(quiz.editors ?? [])]}>
      <QuizContext.Provider value={context}>
        <Page>
          <Page.Heading
            title={quiz.title}
            className='h-[85px]'
            back
            milestone={milestone}
          />
          <NestedNavigation routes={routes} />
        </Page>
      </QuizContext.Provider>
    </PermissionCheckWrapper>
  )
}

export const Route = createFileRoute('/quiz_/$quizId')({
  component: QuizPage,
})
